/**
 * Measuring the sign-in step, because ADR-0008 asked for exactly this.
 *
 * That ADR overrides PRD ID-1 ("score a match within 90 seconds of first open") to put a
 * login in front of the scoring screen, and says plainly what it wants back:
 *
 *   > ID-1 will not be met in P0 and should not be measured as a pass/fail. Observe the
 *   > naive-user field test at the sign-in step specifically — stalling there is a real
 *   > finding, and is the evidence that would justify revisiting this.
 *
 * So this records what would *constitute* that evidence, and nothing else: how long the
 * scorer sat on the sign-in screen, how many attempts it took, what failed each time,
 * and which method finally worked. Four numbers and a list. Enough to say "he took 74
 * seconds and failed twice on the password" in the field-test notes (#16), which is the
 * argument for or against keeping the gate.
 *
 * **Persisted, because Google's flow reloads the page.** The redirect to the vendor and
 * back destroys every bit of in-memory state, so a timer living in a React ref would
 * measure the wrong thing — and measuring email sign-in but not Google would bias the
 * finding towards whichever is slower. `localStorage` is the honest store for it: this is
 * a local observation for a human to read off one device, not telemetry, and P0 ships no
 * analytics endpoint to send it to.
 *
 * Pure apart from the injected clock and store, so it is testable without a browser.
 */
import type { Store } from "../localStore.ts";

/**
 * How the session was obtained.
 *
 * The two email values are spelled out rather than folded into one, because creating an
 * account costs more than signing in and the field test needs to see which it was. Both
 * start `email_` so the email-versus-Google comparison ADR-0008 cares about is one prefix
 * test rather than a list to keep in step.
 *
 * `returning` is its own value too: a valid cookie says nothing about how it was first
 * issued, and attributing it to either method would skew that same comparison.
 */
export type SignInMethod = "email_sign_in" | "email_sign_up" | "google" | "phone_otp" | "returning";

export interface FrictionFailure {
  readonly method: SignInMethod;
  /** An error `code` or a Google `reason` slug — a key, never prose. */
  readonly reason: string;
  readonly at: number;
}

export interface FrictionRecord {
  /** When the sign-in screen was first shown, in epoch ms. */
  readonly shownAt: number;
  readonly attempts: number;
  readonly failures: readonly FrictionFailure[];
  readonly signedInAt: number | null;
  readonly method: SignInMethod | null;
}

export const FRICTION_KEY = "sportsos.signin.friction";

export interface FrictionReport extends FrictionRecord {
  /** Wall-clock seconds from the sign-in screen appearing to a session existing. */
  readonly secondsToSignIn: number | null;
  /**
   * Whether this run is evidence of the friction ADR-0008 predicted.
   *
   * More than one attempt, or over a minute on one screen whose whole content is two
   * fields and a button. A deliberately blunt threshold: its job is to make a finding
   * legible in field notes, not to be a metric anyone tunes.
   */
  readonly stalled: boolean;
}

const STALL_SECONDS = 60;

function read(store: Store): FrictionRecord | null {
  const raw = store.getItem(FRICTION_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<FrictionRecord>;
    if (typeof parsed.shownAt !== "number") return null;
    return {
      shownAt: parsed.shownAt,
      attempts: typeof parsed.attempts === "number" ? parsed.attempts : 0,
      failures: Array.isArray(parsed.failures) ? parsed.failures : [],
      signedInAt: typeof parsed.signedInAt === "number" ? parsed.signedInAt : null,
      method: parsed.method ?? null,
    };
  } catch {
    // Hand-edited or truncated storage is not worth a crash on the login screen, which
    // would lock the scorer out of the app over a measurement.
    return null;
  }
}

export function frictionReport(record: FrictionRecord): FrictionReport {
  const secondsToSignIn =
    record.signedInAt === null ? null : Math.round((record.signedInAt - record.shownAt) / 1000);
  return {
    ...record,
    secondsToSignIn,
    stalled: record.attempts > 1 || (secondsToSignIn !== null && secondsToSignIn > STALL_SECONDS),
  };
}

export interface FrictionObserver {
  /** The sign-in screen is on screen. Starts the clock; a second call does not restart it. */
  shown(): void;
  /** The scorer submitted, or was sent to Google. */
  attempt(method: SignInMethod): void;
  failure(method: SignInMethod, reason: string): void;
  succeeded(method: SignInMethod): void;
  report(): FrictionReport | null;
}

export function frictionObserver(store: Store, now: () => number): FrictionObserver {
  const write = (record: FrictionRecord): void => {
    try {
      store.setItem(FRICTION_KEY, JSON.stringify(record));
    } catch {
      // Private browsing and a full quota both throw on write. Losing the measurement is
      // acceptable; failing the sign-in it was measuring is not.
    }
  };

  const current = (): FrictionRecord => {
    const existing = read(store);
    if (existing !== null) return existing;
    const fresh: FrictionRecord = {
      shownAt: now(),
      attempts: 0,
      failures: [],
      signedInAt: null,
      method: null,
    };
    write(fresh);
    return fresh;
  };

  return {
    shown: () => {
      const existing = read(store);
      // A completed record belongs to a previous session — the scorer signed out and is
      // back. Start a fresh measurement rather than resuming one whose clock started
      // yesterday, which would report a sign-in that took eleven hours.
      if (existing === null || existing.signedInAt !== null) {
        write({ shownAt: now(), attempts: 0, failures: [], signedInAt: null, method: null });
      }
    },

    attempt: (method) => {
      const record = current();
      write({ ...record, attempts: record.attempts + 1, method });
    },

    failure: (method, reason) => {
      const record = current();
      write({ ...record, failures: [...record.failures, { method, reason, at: now() }] });
    },

    succeeded: (method) => {
      const existing = read(store);
      // A *completed* record is last session's, and resuming it would date this sign-in
      // from whenever that screen was shown — so a returning scorer whose cookie is still
      // valid reports a sign-in that took eleven hours and trips the stall warning every
      // single boot, poisoning the one measurement ADR-0008 asked for. He reached a session
      // without a screen, so the honest number is a fresh record timed from now.
      const record =
        existing === null || existing.signedInAt !== null
          ? { shownAt: now(), attempts: 0, failures: [], signedInAt: null, method: null }
          : existing;
      write({
        ...record,
        // Bumped when nothing bumped it: the Google attempt was recorded before a full page
        // reload, and a returning scorer never passed through `attempt` at all.
        attempts: Math.max(1, record.attempts),
        signedInAt: now(),
        method,
      });
    },

    report: () => {
      const record = read(store);
      return record === null ? null : frictionReport(record);
    },
  };
}
